import { CheckCircleFilled, CloseOutlined, FileExcelOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Card, Divider, message, Space, Tooltip, Typography, Upload } from "antd";
import React, { useState } from "react";
import * as XLSX from "xlsx-js-style";
import { highlightWorkbook, readWorkbook } from "./order_highlighter_diff";

const { Title, Text } = Typography;

type SelectedFile = { originFileObj: File; name: string; size: number } | null;

const formatSize = (bytes: number) => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const stripExtension = (name: string) => name.replace(/\.(xlsx|xlsm|xls)$/i, "");

interface FileRowProps {
	label: string;
	hint: string;
	file: SelectedFile;
	disabled: boolean;
	onSelect: (file: SelectedFile) => void;
}

const FileRow = ({ label, hint, file, disabled, onSelect }: FileRowProps) => {
	const beforeUpload = (raw: File) => {
		const name = raw.name.toLowerCase();
		if (!name.endsWith(".xlsx") && !name.endsWith(".xlsm") && !name.endsWith(".xls")) {
			message.error("Only Excel files (.xlsx, .xlsm, .xls) are allowed!");
			return Upload.LIST_IGNORE;
		}

		onSelect({ originFileObj: raw, name: raw.name, size: raw.size });
		return false;
	};

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 16,
				padding: "16px 20px",
				border: `1px solid ${file ? "#b7eb8f" : "#f0f0f0"}`,
				background: file ? "#f6ffed" : "#fafafa",
				borderRadius: 8,
				transition: "all 0.2s",
			}}
		>
			<div style={{ flex: 1, minWidth: 0 }}>
				<Text strong style={{ display: "block" }}>
					{label}
				</Text>
				{file ? (
					<Space size={6} style={{ marginTop: 4 }}>
						<FileExcelOutlined style={{ color: "#52c41a" }} />
						<Text ellipsis style={{ maxWidth: 320 }} title={file.name}>
							{file.name}
						</Text>
						<Text type="secondary">({formatSize(file.size)})</Text>
						<Tooltip title="Remove">
							<Button
								type="text"
								size="small"
								icon={<CloseOutlined />}
								disabled={disabled}
								onClick={() => onSelect(null)}
							/>
						</Tooltip>
					</Space>
				) : (
					<Text type="secondary" style={{ fontSize: 12 }}>
						{hint}
					</Text>
				)}
			</div>

			<Upload beforeUpload={beforeUpload} fileList={[]} maxCount={1} disabled={disabled}>
				<Button icon={<UploadOutlined />} disabled={disabled}>
					{file ? "Replace" : "Browse"}
				</Button>
			</Upload>
		</div>
	);
};

const OrderHighlighter = () => {
	const [previousFile, setPreviousFile] = useState<SelectedFile>(null);
	const [newFile, setNewFile] = useState<SelectedFile>(null);
	const [loading, setLoading] = useState<boolean>(false);

	const ready = Boolean(previousFile && newFile);

	const handleCancel = () => {
		setPreviousFile(null);
		setNewFile(null);
	};

	const handleGo = async () => {
		if (!previousFile || !newFile) {
			message.error("Please select both spreadsheets.");
			return;
		}

		setLoading(true);
		const hideLoading = message.loading("Comparing spreadsheets...", 0);

		try {
			const [wbOld, wbNew] = await Promise.all([
				readWorkbook(previousFile.originFileObj),
				readWorkbook(newFile.originFileObj),
			]);

			const { workbook, summary: result } = highlightWorkbook(wbOld, wbNew);

			const totals = result.reduce(
				(acc, s) => ({
					added: acc.added + s.added,
					revised: acc.revised + s.revised,
					canceled: acc.canceled + s.canceled,
				}),
				{ added: 0, revised: 0, canceled: 0 }
			);

			XLSX.writeFile(workbook, `${stripExtension(newFile.name)} - highlighted.xlsx`, {
				cellStyles: true,
			});

			hideLoading();

			if (!totals.added && !totals.revised && !totals.canceled) {
				message.info("No differences found — the highlighted file was still downloaded.");
			} else {
				message.success("Highlighted spreadsheet downloaded");
			}
		} catch (error: any) {
			console.error("Order highlighter error:", error);
			hideLoading();
			message.error(`Comparison failed: ${error.message || "Unknown error"}`);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Card
			title={
				<div style={{ padding: "4px 0" }}>
					<Title level={4} style={{ marginBottom: 4 }}>
						Order Highlighter
					</Title>
					<Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
						Compare two order spreadsheets and download the new one with the changes highlighted.
					</Text>
				</div>
			}
			headStyle={{ padding: "20px 24px" }}
			bodyStyle={{ padding: 24 }}
			style={{ maxWidth: 760, margin: "24px auto", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
			bordered={true}
		>
			<Space direction="vertical" size={12} style={{ width: "100%" }}>
				<FileRow
					label="Previous spreadsheet"
					hint="The earlier version to compare against"
					file={previousFile}
					disabled={loading}
					onSelect={setPreviousFile}
				/>
				<FileRow
					label="New spreadsheet"
					hint="The latest version — this is the file you get back, highlighted"
					file={newFile}
					disabled={loading}
					onSelect={setNewFile}
				/>
			</Space>
			<Divider style={{ margin: "24px 0 16px" }} />
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Text type="secondary" style={{ fontSize: 12 }}>
					{ready ? (
						<>
							<CheckCircleFilled style={{ color: "#52c41a", marginRight: 6 }} />
							Both files selected — ready to compare
						</>
					) : (
						"Accepts .xlsx, .xlsm and .xls files"
					)}
				</Text>

				<Space>
					<Button onClick={handleCancel} disabled={loading || (!previousFile && !newFile)}>
						Cancel
					</Button>
					<Button type="primary" onClick={handleGo} disabled={!ready || loading} loading={loading}>
						{loading ? "Processing..." : "Go"}
					</Button>
				</Space>
			</div>
		</Card>
	);
};

export default OrderHighlighter;
